/**
 * Multi-Variant Cart — TTSSM-118
 *
 * Handles batch add-to-cart for products with a multi-select checkbox option.
 * Activated when <high-variant-selects data-multi-select-active> is present in
 * the same section.  product-form.js delegates its submit handler here.
 *
 * Public surface:
 *   window.MultiVariantCart.handleSubmit(productFormEl, submitEvent)
 */
(function () {
  "use strict";

  // ─── helpers ────────────────────────────────────────────────────────────────

  /**
   * Return the <high-variant-selects> element for a given section ID.
   * @param {string} sectionId
   * @returns {HTMLElement|null}
   */
  function getVariantSelects(sectionId) {
    return document.getElementById(`Variant-Selects-${sectionId}`);
  }

  /**
   * Return the fieldset element that carries the multi-select checkboxes.
   * @param {HTMLElement} variantSelects
   * @returns {HTMLElement|null}
   */
  function getMultiSelectFieldset(variantSelects) {
    return variantSelects.querySelector("fieldset[data-multi-select-fieldset]");
  }

  /**
   * Return all enabled, checked checkbox inputs within the multi-select fieldset.
   * @param {HTMLElement} variantSelects
   * @returns {HTMLInputElement[]}
   */
  function getCheckedBoxes(variantSelects) {
    const fieldset = getMultiSelectFieldset(variantSelects);
    if (!fieldset) return [];
    return Array.from(
      fieldset.querySelectorAll("input[type=checkbox]:checked:not(:disabled)"),
    );
  }

  /**
   * Return all option rows (fieldsets / divs) within <high-variant-selects>,
   * ordered by their DOM position (= option position order).
   * @param {HTMLElement} variantSelects
   * @returns {HTMLElement[]}
   */
  function getOptionRows(variantSelects) {
    return Array.from(
      variantSelects.querySelectorAll(
        ":scope > fieldset.product-form-input, :scope > div.product-form-input",
      ),
    );
  }

  /**
   * For each option row, return the currently selected option-value ID string.
   * Multi-select rows return null (their value is iterated externally).
   * @param {HTMLElement[]} rows
   * @returns {(string|null)[]}
   */
  function getSingleSelectIds(rows) {
    return rows.map((row) => {
      if (row.hasAttribute("data-multi-select-fieldset")) return null;

      const radio = row.querySelector("input[type=radio]:checked");
      if (radio?.dataset?.optionValueId) return radio.dataset.optionValueId;

      const sel = row.querySelector("select.select-select");
      if (sel?.options?.length) {
        const opt = sel.options[sel.selectedIndex];
        return opt?.dataset?.optionValueId || null;
      }
      return null;
    });
  }


  /**
   * Resolve the current quantity from the section's qty input (if present).
   * @param {string} sectionId
   * @returns {number}
   */
  function getCurrentQty(sectionId) {
    const qtyInput = document.querySelector(
      `#Quantity-${sectionId}.quantity-input, #Quantity-Selector-${sectionId} .quantity-input`,
    );
    return Math.max(1, parseInt(qtyInput?.value, 10) || 1);
  }

  /**
   * Update the ATC button text and disabled state based on how many checkboxes
   * are currently checked and the current qty setting.
   * @param {HTMLElement} variantSelects
   * @param {HTMLElement} submitBtn  — the primary ATC button
   * @param {HTMLElement|null} miniBtn — the mini-checkout submit button, if present
   */
  function updateButtonState(variantSelects, submitBtn, miniBtn) {
    if (!submitBtn) return;

    const sectionId = variantSelects.dataset.section;
    const count = getCheckedBoxes(variantSelects).length;
    const qty = getCurrentQty(sectionId);
    const btnText = submitBtn.querySelector(".button-text");

    if (count === 0) {
      submitBtn.setAttribute("disabled", "disabled");
      if (miniBtn) miniBtn.setAttribute("disabled", "disabled");
      if (btnText) btnText.textContent = "Add to Cart";
    } else if (count === 1 && qty === 1) {
      submitBtn.removeAttribute("disabled");
      if (miniBtn) miniBtn.removeAttribute("disabled");
      if (btnText) btnText.textContent = "Add to Cart";
    } else {
      submitBtn.removeAttribute("disabled");
      if (miniBtn) miniBtn.removeAttribute("disabled");
      const totalUnits = count * qty;
      if (btnText) {
        btnText.textContent =
          qty > 1
            ? `Add ${count} × ${qty} to Cart (${totalUnits} items)`
            : `Add ${count} Items to Cart`;
      }
    }
  }

  /**
   * Toggle all enabled checkboxes on/off and update the toggle button label.
   * @param {HTMLElement} variantSelects
   * @param {HTMLButtonElement} toggleBtn
   */
  function handleToggleAll(variantSelects, toggleBtn) {
    const fieldset = getMultiSelectFieldset(variantSelects);
    if (!fieldset) return;

    const checkboxes = Array.from(
      fieldset.querySelectorAll("input[type=checkbox]:not(:disabled)"),
    );
    const allChecked = checkboxes.every((cb) => cb.checked);
    checkboxes.forEach((cb) => (cb.checked = !allChecked));
    toggleBtn.textContent = allChecked ? "Select All" : "Deselect All";
  }


  // ─── init ───────────────────────────────────────────────────────────────────

  /**
   * Attach checkbox + toggle listeners to a single <high-variant-selects>
   * element that has multi-select active.
   * @param {HTMLElement} variantSelects
   */
  function initElement(variantSelects) {
    if (!getMultiSelectFieldset(variantSelects)) {
      variantSelects.removeAttribute("data-multi-select-active");
      return;
    }

    const sectionId = variantSelects.dataset.section;
    const submitBtn = document.getElementById(
      `Product-Submit-Button-${sectionId}`,
    );
    const miniBtn = document.querySelector(
      `#Product-Mini-Checkout-${sectionId} button[type=submit]`,
    );

    // Disable ATC immediately — nothing is checked on page load
    updateButtonState(variantSelects, submitBtn, miniBtn);

    const dynamicCheckout = document.querySelector(
      `product-form[data-section="${sectionId}"] .dynamic-checkout`,
    );
    if (dynamicCheckout) {
      dynamicCheckout.addEventListener(
        "click",
        (event) => {
          event.preventDefault();
          event.stopImmediatePropagation();
          const productForm = dynamicCheckout.closest("product-form");
          if (productForm && getCheckedBoxes(variantSelects).length) {
            handleSubmit(productForm, event, true);
          }
        },
        true,
      );
    }

    variantSelects.addEventListener("change", (e) => {
      if (e.target.type === "checkbox") {
        updateButtonState(variantSelects, submitBtn, miniBtn);
      }
    });

    // Keep button label in sync when the quantity input changes
    const qtyInput = document.querySelector(
      `#Quantity-${sectionId}.quantity-input, #Quantity-Selector-${sectionId} .quantity-input`,
    );
    if (qtyInput) {
      qtyInput.addEventListener("change", () =>
        updateButtonState(variantSelects, submitBtn, miniBtn),
      );
      qtyInput.addEventListener("input", () =>
        updateButtonState(variantSelects, submitBtn, miniBtn),
      );
    }

    variantSelects.addEventListener("click", (e) => {
      const toggle = e.target.closest("[data-multi-select-toggle]");
      if (toggle) {
        handleToggleAll(variantSelects, toggle);
        updateButtonState(variantSelects, submitBtn, miniBtn);
      }
    });
  }

  /**
   * Scan the page for any multi-select variant pickers and initialise them.
   */
  function init() {
    document
      .querySelectorAll("high-variant-selects[data-multi-select-active]")
      .forEach(initElement);
  }

  // ─── submit handler ─────────────────────────────────────────────────────────

  /**
   * Called by ProductForm.onSubmitHandler when multi-select mode is active.
   * Resolves variant IDs, performs batch /cart/add.js, then triggers section
   * updates + cart drawer exactly as the normal single-add flow does.
   *
   * @param {ProductForm} productForm  — the custom element instance
   * @param {Event}       event        — the original submit event
   * @param {boolean}     [goToCheckout] — Buy It Now: add selected items, then /checkout
   */
  async function handleSubmit(productForm, event, goToCheckout) {
    const sectionId = productForm.dataset.section;
    const variantSelects = getVariantSelects(sectionId);
    if (!variantSelects) return;

    if (!getMultiSelectFieldset(variantSelects)) {
      variantSelects.removeAttribute("data-multi-select-active");
      return;
    }

    const checkedBoxes = getCheckedBoxes(variantSelects);
    if (checkedBoxes.length === 0) return;

    // Ensure the variant map is fresh
    window.variantOptionMapHelpers.refreshVariantOptionMap(variantSelects);
    const variantMap = variantSelects.variantOptionMap;

    // Build the positional ID array for all option rows
    const allRows = getOptionRows(variantSelects);
    const singleSelectIds = getSingleSelectIds(allRows);
    const msRowIdx = allRows.findIndex((r) =>
      r.hasAttribute("data-multi-select-fieldset"),
    );

    // Read quantity from the section's quantity input if one is present,
    // otherwise default to 1 per variant.
    const qtyInput = document.querySelector(
      `#Quantity-${sectionId}.quantity-input, #Quantity-Selector-${sectionId} .quantity-input`,
    );
    const quantity = Math.max(1, parseInt(qtyInput?.value, 10) || 1);

    const items = [];

    for (const checkbox of checkedBoxes) {
      const checkValueId = checkbox.dataset.optionValueId;

      // Swap the multi-select position with this checkbox's value ID
      const ids = [...singleSelectIds];
      ids[msRowIdx] = checkValueId;

      const variantId = window.variantOptionMapHelpers.lookupVariantByOptionValueIds(
        variantMap,
        ids,
      );

      if (!variantId) {
        // Variant not in map — skip silently (shouldn't happen with correct data)
        continue;
      }

      items.push({ id: variantId, quantity });
    }

    if (items.length === 0) return;

    productForm.handleErrorMessage();
    productForm.startLoading();

    const submitBtn = document.getElementById(
      `Product-Submit-Button-${sectionId}`,
    );
    const miniBtn = document.querySelector(
      `#Product-Mini-Checkout-${sectionId} button[type=submit]`,
    );

    try {
      const sectionsToRender = productForm.getSectionsToRender();

      const response = await fetch("/cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: JSON.stringify({
          items,
          sections: sectionsToRender.map((s) => s.section).join(","),
          sections_url: window.location.pathname,
        }),
      });

      const data = await response.json();

      if (data.status || data.errors) {
        productForm.handleErrorMessage(
          data.description || data.errors || "Could not add items to cart.",
        );
        return;
      }

      // Update section HTML (cart icon bubble, cart drawer contents)
      if (typeof SectionDynamicUpdate !== "undefined") {
        SectionDynamicUpdate.updateSections(sectionsToRender, data.sections);
      }

      // Broadcast cart update so other components (totals etc.) can react
      webvista.publish(PUB_SUB_EVENTS.cartUpdate, {
        source: "product-form",
        cartData: data,
      });

      if (goToCheckout) {
        window.location = `${window.Shopify?.routes?.root || "/"}checkout`;
        return;
      }

      // Open cart drawer
      if (
        productForm.cartDrawer &&
        !productForm.cartDrawer.hasAttribute("data-status-silence")
      ) {
        productForm.cartDrawer.show(event?.submitter);
      } else {
        window.location = window.routes?.cart_url || "/cart";
      }

      // Uncheck all boxes so the button resets to disabled state
      const fieldset = getMultiSelectFieldset(variantSelects);
      fieldset
        ?.querySelectorAll("input[type=checkbox]")
        .forEach((cb) => (cb.checked = false));

      // Also reset the Select All toggle label
      const toggle = fieldset?.querySelector("[data-multi-select-toggle]");
      if (toggle) toggle.textContent = "Select All";
    } catch {
      productForm.handleErrorMessage(
        window?.accessibilityStrings?.unknownError || "An unexpected error occurred.",
      );
    } finally {
      productForm.endLoading();
      // Re-evaluate button state — endLoading() re-enables all buttons, but
      // with 0 boxes checked we want the ATC button disabled again.
      updateButtonState(variantSelects, submitBtn, miniBtn);
    }
  }

  // ─── boot ────────────────────────────────────────────────────────────────────

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  window.MultiVariantCart = { handleSubmit, init };
})();
